import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import type { SimplifiedSiteStatus } from '../../shared/api/executive-monitoring.api'

export interface WebSocketConfig {
  url: string
  reconnectDelay: number
  maxReconnectAttempts: number
  heartbeatInterval: number
}

export interface WebSocketMessage<T = unknown> {
  type: string
  data: T
  timestamp: string
}

export interface WebSocketConnection {
  isConnected: boolean
  lastMessage: WebSocketMessage | null
  executiveData: SimplifiedSiteStatus[]
  sendMessage: (type: string, data: unknown) => boolean
  disconnect: () => void
  reconnect: () => void
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error'
  error: Error | null
  pingLatency: number | null
}

const DEFAULT_CONFIG: WebSocketConfig = {
  url: '/ws',
  reconnectDelay: 5000,
  maxReconnectAttempts: 10,
  heartbeatInterval: 30000
}

export const useWebSocket = (config: Partial<WebSocketConfig> = {}): WebSocketConnection => {
  const finalConfig: WebSocketConfig = useMemo(() => ({
    ...DEFAULT_CONFIG,
    ...config
  }), [config])

  const [isConnected, setIsConnected] = useState<boolean>(false)
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null)
  const [executiveData, setExecutiveData] = useState<SimplifiedSiteStatus[]>([])
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('connecting')
  const [error, setError] = useState<Error | null>(null)
  const [pingLatency, setPingLatency] = useState<number | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef<number>(0)
  const reconnectTimeoutRef = useRef<number | undefined>(undefined)
  const heartbeatIntervalRef = useRef<number | undefined>(undefined)
  const pingSentTimeRef = useRef<number>(0)

  const handleWebSocketError = useCallback((error: Error) => {
    console.error('WebSocket error:', error)
    setError(error)
    setConnectionStatus('error')
  }, [])

  const handleWebSocketClose = useCallback((event: CloseEvent) => {
    console.log('WebSocket disconnected:', {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean
    })

    setIsConnected(false)
    setConnectionStatus('disconnected')
    
    // Очищаем интервалы
    if (heartbeatIntervalRef.current !== undefined) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = undefined
    }

    // Экспоненциальный backoff для переподключения
    if (reconnectAttemptsRef.current < finalConfig.maxReconnectAttempts) {
      const delay = Math.min(
        finalConfig.reconnectDelay * Math.pow(1.5, reconnectAttemptsRef.current),
        30000 // Максимум 30 секунд
      )
      
      reconnectAttemptsRef.current += 1
      
      console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${finalConfig.maxReconnectAttempts})`)
      
      reconnectTimeoutRef.current = window.setTimeout(() => {
        if (wsRef.current) {
          wsRef.current.close()
        }
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const wsUrl = `${protocol}//${window.location.host}${finalConfig.url}`
        wsRef.current = new WebSocket(wsUrl)
      }, delay)
    } else {
      console.error('Max reconnection attempts reached')
      setError(new Error('Unable to establish WebSocket connection after multiple attempts'))
    }
  }, [finalConfig.maxReconnectAttempts, finalConfig.reconnectDelay, finalConfig.url])

  const handleWebSocketMessage = useCallback((event: MessageEvent) => {
    try {
      const message: WebSocketMessage = JSON.parse(event.data)
      setLastMessage(message)

      // Обрабатываем разные типы сообщений
      switch (message.type) {
        case 'pong':
          if (pingSentTimeRef.current > 0) {
            const latency = Date.now() - pingSentTimeRef.current
            setPingLatency(latency)
            pingSentTimeRef.current = 0
          }
          break

        case 'executive_monitoring':
          if (Array.isArray(message.data)) {
            setExecutiveData(message.data as SimplifiedSiteStatus[])
          }
          break

        case 'incident_created':
        case 'incident_resolved':
          // Оповещения об инцидентах можно логировать
          console.log('Incident notification:', message.type, message.data)
          break

        default:
          console.debug('Received WebSocket message:', message.type)
      }
    } catch (err) {
      handleWebSocketError(err instanceof Error ? err : new Error('Failed to parse WebSocket message'))
    }
  }, [handleWebSocketError])

  const handleWebSocketOpen = useCallback(() => {
    console.log('✅ WebSocket connected successfully')
    setIsConnected(true)
    setConnectionStatus('connected')
    setError(null)
    reconnectAttemptsRef.current = 0

    // Подписываемся на события
    wsRef.current?.send(JSON.stringify({
      type: 'subscribe',
      data: { events: ['executive_monitoring', 'incident_created', 'incident_resolved'] },
      timestamp: new Date().toISOString()
    }))

    // Настраиваем heartbeat
    heartbeatIntervalRef.current = window.setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        pingSentTimeRef.current = Date.now()
        wsRef.current.send(JSON.stringify({
          type: 'ping',
          data: { timestamp: new Date().toISOString() },
          timestamp: new Date().toISOString()
        }))
      }
    }, finalConfig.heartbeatInterval)

  }, [finalConfig.heartbeatInterval])

  const connect = useCallback(() => {
    // Очищаем предыдущее соединение
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    if (reconnectTimeoutRef.current !== undefined) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = undefined
    }

    setConnectionStatus('connecting')
    
    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const wsUrl = `${protocol}//${window.location.host}${finalConfig.url}`
      const ws = new WebSocket(wsUrl)
      
      ws.onopen = handleWebSocketOpen
      ws.onmessage = handleWebSocketMessage
      ws.onclose = handleWebSocketClose
      ws.onerror = () => {
        handleWebSocketError(new Error('WebSocket error'))
      }

      wsRef.current = ws
    } catch (err) {
      handleWebSocketError(err instanceof Error ? err : new Error('Failed to create WebSocket'))
    }
  }, [finalConfig.url, handleWebSocketOpen, handleWebSocketMessage, handleWebSocketClose, handleWebSocketError])

  const disconnect = useCallback(() => {
    console.log('Disconnecting WebSocket...')
    
    // Очищаем все таймеры
    if (reconnectTimeoutRef.current !== undefined) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = undefined
    }

    if (heartbeatIntervalRef.current !== undefined) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = undefined
    }

    // Закрываем соединение
    if (wsRef.current) {
      // Отправляем сообщение о корректном отключении
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'unsubscribe',
          data: { events: ['executive_monitoring'] },
          timestamp: new Date().toISOString()
        }))
        
        // Даем время на обработку отписки
        setTimeout(() => {
          wsRef.current?.close(1000, 'Client disconnect')
        }, 100)
      } else {
        wsRef.current.close()
      }
      wsRef.current = null
    }

    setConnectionStatus('disconnected')
    setIsConnected(false)
    setExecutiveData([])
    setLastMessage(null)
    reconnectAttemptsRef.current = 0
  }, [])

  const sendMessage = useCallback((type: string, data: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message: WebSocketMessage = {
        type,
        data,
        timestamp: new Date().toISOString()
      }
      
      try {
        wsRef.current.send(JSON.stringify(message))
        return true
      } catch (err) {
        console.error('Failed to send WebSocket message:', err)
        return false
      }
    } else {
      console.warn('Cannot send message: WebSocket is not connected')
      return false
    }
  }, [])

  const reconnect = useCallback(() => {
    console.log('Manual reconnect requested')
    disconnect()
    setTimeout(() => connect(), 100)
  }, [connect, disconnect])

  // Инициализация соединения при монтировании
  useEffect(() => {
    const timer = setTimeout(() => {
      connect()
    }, 100)

    return () => {
      clearTimeout(timer)
      disconnect()
    }
  }, [connect, disconnect])

  // Возвращаем состояние соединения
  return useMemo<WebSocketConnection>(() => ({
    isConnected,
    lastMessage,
    executiveData,
    sendMessage,
    disconnect,
    reconnect,
    connectionStatus,
    error,
    pingLatency
  }), [
    isConnected,
    lastMessage,
    executiveData,
    sendMessage,
    disconnect,
    reconnect,
    connectionStatus,
    error,
    pingLatency
  ])
}